import { useState, useEffect, useRef, useCallback } from 'react';

interface IntegrationSettingsProps {
  serverUrl: string;
  onStreamlabsConfigured?: (hasToken: boolean) => void;
}

interface StreamlabsSettings {
  enabled: boolean;
  socketToken: string;
  pauseDuringAlerts: boolean;
}

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

export function IntegrationSettings({ serverUrl, onStreamlabsConfigured }: IntegrationSettingsProps) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const [streamlabs, setStreamlabs] = useState<StreamlabsSettings>({
    enabled: false,
    socketToken: '',
    pauseDuringAlerts: true,
  });

  // Track if initial load is complete to avoid saving on mount
  const initialLoadComplete = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Fetch streamlabs settings
    fetch(`${serverUrl}/api/settings/streamlabs`, {
      credentials: 'include',
    })
      .then((res) => res.json() as Promise<{ error?: string; streamlabs?: StreamlabsSettings }>)
      .then((data) => {
        if (data && !data.error && data.streamlabs) {
          setStreamlabs({
            enabled: data.streamlabs.enabled ?? false,
            socketToken: data.streamlabs.socketToken ?? '',
            pauseDuringAlerts: data.streamlabs.pauseDuringAlerts ?? true,
          });
          // Notify parent of streamlabs configuration status
          onStreamlabsConfigured?.(!!data.streamlabs.socketToken);
        }
        initialLoadComplete.current = true;
      })
      .catch(() => {
        initialLoadComplete.current = true;
      });
  }, [serverUrl, onStreamlabsConfigured]);

  const saveSettings = useCallback(async (settings: StreamlabsSettings) => {
    setSaveStatus('saving');
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/streamlabs`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      if (res.ok) {
        setSaveStatus('saved');
        // Notify parent of new configuration status
        onStreamlabsConfigured?.(!!settings.socketToken);
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [serverUrl, onStreamlabsConfigured]);

  // Debounced save for text inputs
  const debouncedSave = useCallback((settings: StreamlabsSettings) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      void saveSettings(settings);
    }, 500);
  }, [saveSettings]);

  // Immediate save for toggles
  const handleToggle = useCallback((field: 'enabled' | 'pauseDuringAlerts') => {
    if (!initialLoadComplete.current) return;
    const newSettings = { ...streamlabs, [field]: !streamlabs[field] };
    setStreamlabs(newSettings);
    void saveSettings(newSettings);
  }, [streamlabs, saveSettings]);

  // Debounced save for text
  const handleTokenChange = useCallback((value: string) => {
    const newSettings = { ...streamlabs, socketToken: value };
    setStreamlabs(newSettings);
    if (initialLoadComplete.current) {
      debouncedSave(newSettings);
    }
  }, [streamlabs, debouncedSave]);

  return (
    <div className="space-y-4">
      {saveStatus !== 'idle' && (
        <div className="flex items-center justify-end">
          <div className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest transition-all duration-500 ${saveStatus === 'saving' ? 'bg-cyan-500/10 text-cyan-300 animate-pulse' :
            saveStatus === 'saved' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'
            }`}>
            {saveStatus === 'saving' ? 'Syncing...' :
              saveStatus === 'saved' ? 'Synced to Cloud' : 'Sync Error'}
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-[28px] border border-emerald-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.82),rgba(8,37,43,0.72)_54%,rgba(34,19,54,0.66))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.24)]">
        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-400/12 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-72 bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="mb-3 inline-flex rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-emerald-100">
              Streamlabs
            </div>
            <h3 className="text-2xl font-black text-white">Connect alerts and donations</h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300/72">
              Use Streamlabs events so StreamBuddy can react to donations and pause while alerts play.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 xl:min-w-[420px]">
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Integration</div>
              <div className={`mt-1 text-sm font-black ${streamlabs.enabled ? 'text-emerald-300' : 'text-slate-400'}`}>{streamlabs.enabled ? 'On' : 'Off'}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Token</div>
              <div className={`mt-1 text-sm font-black ${streamlabs.socketToken ? 'text-cyan-100' : 'text-slate-400'}`}>{streamlabs.socketToken ? 'Saved' : 'Missing'}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Alerts</div>
              <div className={`mt-1 text-sm font-black ${streamlabs.pauseDuringAlerts ? 'text-fuchsia-100' : 'text-slate-400'}`}>{streamlabs.pauseDuringAlerts ? 'Pause voice' : 'Keep talking'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(360px,1.1fr)]">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-[24px] border border-white/10 bg-black/20 p-4">
            <div>
              <span className="text-sm font-black text-white">Use Streamlabs events</span>
              <p className="mt-1 text-xs text-slate-400">Turn this on for donations and Streamlabs alert timing.</p>
            </div>
            <button
              onClick={() => handleToggle('enabled')}
              className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${streamlabs.enabled ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-white/15'
                }`}
            >
              <div className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${streamlabs.enabled ? 'translate-x-7' : 'translate-x-1'
                }`} />
            </button>
          </div>

          <div className="rounded-[24px] border border-amber-300/[0.14] bg-amber-400/[0.045] p-4">
            <h4 className="text-sm font-black text-amber-100">Turn off Streamlabs TTS</h4>
            <p className="mt-2 text-xs leading-5 text-slate-300/72">
              StreamBuddy provides the voice. Disable Streamlabs TTS so alerts do not speak over it.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {['Tips', 'Subscriptions', 'Bits'].map((type) => (
                <div key={type} className="rounded-2xl border border-white/10 bg-black/24 px-3 py-2 text-center">
                  <div className="text-[10px] font-black uppercase text-amber-100/70">{type}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-slate-400">TTS OFF</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {streamlabs.enabled ? (
          <div
            className="space-y-4 self-start"
            style={{
              animation: 'pureFadeIn 0.3s ease-out forwards',
              opacity: 0
            }}
          >
            <style>{`
              @keyframes pureFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
            `}</style>
            <div className="space-y-4 rounded-[24px] border border-cyan-300/[0.12] bg-cyan-400/[0.045] p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="text-[10px] font-black uppercase tracking-widest text-cyan-200/80">
                  Socket API Token
                </label>
                <a
                  href="https://streamlabs.com/dashboard#/settings/api-settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-fit rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-cyan-100/75 transition-colors hover:text-cyan-100"
                >
                  Get Token →
                </a>
              </div>
              <input
                type="password"
                value={streamlabs.socketToken}
                onChange={(e) => handleTokenChange(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 font-mono text-sm text-cyan-100 outline-none transition-colors focus:border-cyan-300/60"
                placeholder="eyJ..."
              />
              <p className="text-xs leading-5 text-slate-500">
                In Streamlabs, open API Settings, copy the Socket API Token, and paste it here.
              </p>

              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/18 px-3 py-3">
                  <div className="flex-1">
                    <span className="text-sm font-black text-white">Pause StreamBuddy during alerts</span>
                    <p className="mt-1 text-xs leading-5 text-slate-400">Keeps donations, subs, and bits from talking over StreamBuddy.</p>
                  </div>
                  <button
                    onClick={() => handleToggle('pauseDuringAlerts')}
                    className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${streamlabs.pauseDuringAlerts ? 'bg-cyan-500 shadow-lg shadow-cyan-500/20' : 'bg-white/15'
                      }`}
                  >
                    <div className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${streamlabs.pauseDuringAlerts ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[220px] items-center justify-center rounded-[24px] border border-dashed border-white/10 bg-white/[0.025] p-6 text-center">
            <div>
              <p className="text-sm font-black text-white">Streamlabs is off</p>
              <p className="mt-2 max-w-md text-xs leading-5 text-slate-500">
                Twitch subs and bits can still work without this. Turn Streamlabs on when you want donation support and alert-aware voice pauses.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
